import React, {useContext} from 'react';
import NextApp from 'next/app';
import Head from 'next/head';
import get from 'lodash/get';
import getConfig from 'next/config';
import {ThemeProvider} from 'styled-components';

import Error from '~/src/pages/_error/_error';
import Navigation from '~/src/components/navigation/Navigation';
import {Context} from '~/src/components/context/Context';
import SettingsProvider, {SettingsContext} from '~/src/components/context/SettingsContext';
import SitemapProvider from '~/src/components/context/SitemapContext';
import ConsentProvider from '~/src/components/context/ConsentContext';
import LoginProvider from '~/src/components/context/LoginContext';
import HistoryContextProvider from '~/src/components/context/HistoryContext';
import LoginModal from '~/src/components/login/loginModal/LoginModal';
import LanguageInfoBar from '~/src/components/languageInfoBar/LanguageInfoBar';
import UserProfileProvider from '~/src/components/context/UserProfileContext';
import NavigationStateProvider, {NavigationStateContext} from '~/src/components/context/NavigationStateContext';

import {updateSitemap} from '~/src/utilities/feed/sitemap';
import {resetCounters} from '~/src/utilities/helpers/counters';
import {startReloadTimeout} from '~/src/utilities/helpers/reloader';
import {SiteAreaTheme} from '~/src/utilities/constants/navigation';
import {ContentType} from '~/src/utilities/constants/types';
import {classNames} from '~/src/utilities/helpers/css';
import {getInitialSettings} from '~/src/utilities/helpers/settings';
import {isLegacyAppSession} from '~/src/utilities/helpers/client';
import {isEnabled, updateFeatures} from '~/src/utilities/helpers/feature';
import {pageIsReady} from '~/src/utilities/libs/totm';
import {getRandomKey} from '~/src/utilities/helpers/string';
import {Logger} from '~/src/utilities/helpers/log';
import {extractLanguage} from '~/src/utilities/helpers/language';
import {getRootPath} from '~/src/utilities/helpers/link';
import {Tenant} from '~/src/utilities/constants/tenant';
import {Feature} from '~/src/utilities/constants/feature';
import {sourcepointTrigger, sourcepointInit} from '~/src/utilities/libs/sourcepoint';

import {MessageContextProvider} from '@components/utils/message';
import {getThemes, getMessages} from '~/src/utilities/helpers/brand';

import css from './_app.module.scss';

import '~/src/utilities/libs/rollbar';

const {publicRuntimeConfig: config} = getConfig();

const AppContainer = ({children}) => {
    const {siteArea} = useContext(SettingsContext);
    const {isCollapsed: navigationCollapsed} = useContext(NavigationStateContext);

    const appClassName = classNames(css, 'site', siteArea, {navigationCollapsed});

    return <div className={appClassName}>{children}</div>;
};

class App extends NextApp {
    static async getInitialProps({Component, ctx}) {
        const settings = getInitialSettings(ctx);

        if (!process.browser) {
            const userLanguage = extractLanguage(ctx);

            if (
                userLanguage !== config.language &&
                ctx.req.originalUrl === getRootPath(config.language) &&
                getRootPath(userLanguage) !== getRootPath(config.language)
            ) {
                return ctx.res.redirect(302, getRootPath(userLanguage));
            }
        }

        const sitemap = await updateSitemap();
        resetCounters();

        let pageProps = {};

        if (Component.getInitialProps) {
            pageProps = await Component.getInitialProps(ctx, {settings, sitemap});
            settings.siteArea = pageProps.siteArea;
        }

        const context = await Context.getInitialProps(ctx, settings);

        if (pageProps.error && ctx.res) {
            const statusCode = pageProps.error.statusCode || 500;
            ctx.res.status(statusCode);
        }

        return {...pageProps, pageKey: getRandomKey(), context, sitemap, isFirstPageRequest: !process.browser, settings};
    }

    static getDerivedStateFromError(error) {
        return {error, enableServerRequest: true};
    }

    prepareForLegacyApp() {
        const isLegacyApp = !this.props.settings.isWebView && isLegacyAppSession();

        if (isLegacyApp) {
            this.setState({isLegacyApp});
        }
    }

    async enableServerRequest() {
        await startReloadTimeout();
        this.setState({enableServerRequest: true});
    }

    state = {
        context: this.props.context,
        error: this.props.error,
        isLegacyApp: false,
        enableServerRequest: false,
        settings: this.props.settings
    };

    constructor(props) {
        super(props);
        updateFeatures(this.props.settings.feature);
    }

    componentDidMount() {
        this.prepareForLegacyApp();
        this.enableServerRequest();

        pageIsReady();
    }

    componentDidUpdate() {
        sourcepointTrigger();
        pageIsReady();
    }

    componentDidCatch(error, info) {
        Logger.critical(`[App] Unhandled error`, {error, info});
    }

    render() {
        const {Component, navigation, data, sitemap, settings, isFirstPageRequest, pageKey} = this.props;
        const {enableServerRequest, isLegacyApp, error} = this.state;
        const page = error ? <Error {...error} /> : <Component key={pageKey} {...data} isFirstPageRequest={isFirstPageRequest} />;

        // needs to be in app because it's using css import (not allowed in document)
        const netmetrixProfileInit = `
            var szmvars = '${config.netmetrix.profileName}//CP//';
            var szmopt = {mobile: window.innerWidth < ${parseInt(css.maxWidthTablet, 10)}};
        `;

        const Messages = getMessages();
        const Themes = getThemes();

        return (
            <SettingsProvider {...this.state.settings} {...settings} enableServerRequest={enableServerRequest}>
                <LoginProvider language={config.language}>
                    <UserProfileProvider cacheKey={pageKey}>
                        <SitemapProvider sitemap={sitemap}>
                            <ConsentProvider>
                                <Context context={this.state.context}>
                                    <ThemeProvider theme={Themes.News}>
                                        <MessageContextProvider messages={Messages}>
                                            <HistoryContextProvider>
                                                <Head>
                                                    {/* browser viewport */}
                                                    <meta key="viewport" name="viewport" content="width=device-width, height=device-height, initial-scale=1" />

                                                    {/* app & theme definitions */}
                                                    <meta key="mwac" name="mobile-web-app-capable" content="yes" />
                                                    <meta key="amwac" name="apple-mobile-web-app-capable" content="yes" />
                                                    <meta key="amwasbs" name="apple-mobile-web-app-status-bar-style" content={css.colorSapphire} />

                                                    {/* robots */}
                                                    <meta
                                                        key="robots"
                                                        name="robots"
                                                        content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
                                                    />

                                                    {/* netmetrix */}
                                                    <script key="netmetrixProfileInit" dangerouslySetInnerHTML={{__html: netmetrixProfileInit}}></script>

                                                    {config.tenantId !== Tenant.LEMATIN && <link rel="manifest" href={`/${config.language}.manifest.json`} />}

                                                    <meta name="theme-color" content={SiteAreaTheme[settings.siteArea] || SiteAreaTheme.FRONT} />

                                                    {get(data, 'type') !== ContentType.SLIDESHOW && (
                                                        <meta name="apple-itunes-app" content={`app-id=${config.nativeApps.appleAppStoreId}`} />
                                                    )}

                                                    <script dangerouslySetInnerHTML={sourcepointInit()} />
                                                </Head>

                                                <NavigationStateProvider>
                                                    <AppContainer>
                                                        {isEnabled(Feature.LANGUAGE_INFO_BAR) && !settings.isWebView && !isLegacyApp && <LanguageInfoBar />}
                                                        {!isLegacyApp && <Navigation {...navigation} pageKey={pageKey} />}
                                                        {page}
                                                    </AppContainer>
                                                </NavigationStateProvider>

                                                <LoginModal key={pageKey} />
                                            </HistoryContextProvider>
                                        </MessageContextProvider>
                                    </ThemeProvider>
                                </Context>
                            </ConsentProvider>
                        </SitemapProvider>
                    </UserProfileProvider>
                </LoginProvider>
            </SettingsProvider>
        );
    }
}

export default App;
